import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';

import { AIFilledHintDecoration } from 'components/AIHint';
import { Button } from 'components/Button';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import {
  type CredentialOptionsResponse,
  EMPTY_CREDENTIAL_OPTIONS_RESPONSE,
  EligibleCredential,
} from 'lib/api/getCredentialOptions';
import {
  PortalBidRateBasis,
  type PortalBidRateResolutionRequest,
  getPortalBidRateResolution,
} from 'lib/api/getPortalBidRateResolution';
import { SubmitBidStatusVariant } from 'pages/QuoteView/Quoting/RequestQuickQuote/constants';
import { useQuickQuoteContext } from 'pages/QuoteView/Quoting/RequestQuickQuote/context/QuickQuoteContext';
import {
  getCredentialDisplayLabel,
  useCredentialOptions,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/hooks/useCredentialOptions';
import {
  CarrierCostType,
  QuickQuoteInputs,
  QuoteMode,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import {
  buildPortalSubmitBidAmountStrings,
  getLTLSellRate,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/util';
import { parseLocation } from 'pages/QuoteView/Quoting/RequestQuickQuote/utils/location';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { QuotingPortals, integrationNameMap } from 'types/enums/Integrations';
import { formatCurrency } from 'utils/formatCurrency';
import { calculateFlatCarrierCost } from 'utils/priceCalculations';
import { cn } from 'utils/shadcn';

import { QuickQuoteTextInput } from './QuickQuoteTextInput';
import { getSubmitBidPortalAsyncPollNotice } from './submitBidPortalAsyncPollNotices';
import { getServerSubmitPortalNamesForQuoteMode } from './submitBidPortalPolicy';

const LOGIN_HELP_TOOLTIP_MESSAGE =
  'Want to add another login? Reach out to support@drumkit.ai';

// Shown when submit-bid is enabled but no bidding-portal integrations have saved logins yet.
const NO_PORTALS_CONFIGURED_TOOLTIP_MESSAGE =
  'No bidding portal logins are set up for your organization yet. Reach out to support@drumkit.ai to configure integrations.';

// Narrow sidebar selects: truncate label, pin chevron; full label via native `title` on trigger.
const SUBMIT_BID_SIDEBAR_SELECT_TRIGGER_CLASSNAME = cn(
  'h-8 mt-1 w-full min-w-0 justify-start gap-2',
  '[&>span]:line-clamp-none',
  '[&>:first-child]:min-w-0 [&>:first-child]:grow [&>:first-child]:basis-0 [&>:first-child]:truncate',
  '[&>:last-child]:shrink-0'
);

export interface SubmitBidSectionProps {
  integrationName: Maybe<string>;
  // FTL/LTL: used with `buildPortalSubmitBidAmountStrings` for portal bid amounts in the summary.
  fuelEstimate?: number;
  // Fallback when `getPortalBidRateResolution` cannot run (missing ids, API error, or feature off).
  // Prefer resolved basis from the backend when available.
  submitBidUsesLinehaul?: boolean;
  tmsCustomerId?: string;
  customerName?: string;
  // Batched GET /integrations/credentials/options (one map per portal); from usePortalCredentialOptionsBatch.
  credentialOptionsByPortal: Partial<Record<string, CredentialOptionsResponse>>;
  isPortalCredentialBatchLoading: boolean;
  onSubmitBid?: (payload: SubmitBidPayload) => void | Promise<void>;
  isSubmittingBid?: boolean;
  submitBidStatus?: Maybe<SubmitBidStatus>;
  // True while submit-bid result API is returning HTTP 202 (async job still running).
  isSubmitBidResultPending?: boolean;
  onResolvedSubmitBidUsesLinehaul?: (value: Maybe<boolean>) => void;
  onSelectedPortalChange?: (value: Maybe<string>) => void;
  // Normalized submit-bid load id (form `loadId`, else email `biddingPortalLoadReference`), for bid-rate resolution in the summary.
  normalizedLoadId?: string;
  /** Increments when the parent clears derived submit-bid basis; keeps local resolution in sync. */
  resolvedSubmitBidBasisResetSignal?: number;
}

export interface SubmitBidStatus {
  message: Maybe<string>;
  variant: SubmitBidStatusVariant;
}

function submitBidStatusVariantTextClassName(
  variant: SubmitBidStatusVariant
): string {
  switch (variant) {
    case SubmitBidStatusVariant.Success:
      return 'text-success-600';
    case SubmitBidStatusVariant.Destructive:
      return 'text-error-600';
    case SubmitBidStatusVariant.Warning:
      return 'text-warning-600';
    case SubmitBidStatusVariant.Info:
      return 'text-info-600';
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

export interface SubmitBidPayload {
  integrationName: Maybe<string>;
  credentialId: Maybe<number>;
  // Top-level `integrationId` on GET /integrations/credentials/options (see useCredentialOptions).
  integrationId: Maybe<number>;
  selectedLtlQuoteId: Maybe<number>;
  buyRate: number;
  sellRate: number;
  margin: number;
  // Linehaul vs total-sell at click time (matches summary) avoids state lag after bid-rate resolve.
  effectiveSubmitBidUsesLinehaul: boolean;
}

const PORTAL_VALUES = Object.values(QuotingPortals);

const buildCredentialSelectOptions = (
  credentials: EligibleCredential[],
  recommendedCredentialId: Maybe<number>,
  userLoginHint: Maybe<string>
) =>
  credentials.map((c) => ({
    label: getCredentialDisplayLabel(c, userLoginHint),
    value: String(c.credentialId),
    isRecommended: c.credentialId === recommendedCredentialId,
  }));

const buildStopLabel = (stop: {
  city?: string;
  state?: string;
  zip?: string;
  location?: string;
}): string => {
  if (stop.city) {
    return stop.state ? `${stop.city}, ${stop.state}` : stop.city;
  }

  if (stop.location) {
    const parsed = parseLocation(stop.location);
    if (parsed?.city) {
      return parsed.state ? `${parsed.city}, ${parsed.state}` : parsed.city;
    }
    return stop.location;
  }

  if (stop.zip) {
    return stop.zip;
  }

  return '';
};

export const SubmitBidSection = ({
  integrationName,
  fuelEstimate = 0,
  submitBidUsesLinehaul = false,
  tmsCustomerId,
  customerName,
  credentialOptionsByPortal,
  isPortalCredentialBatchLoading,
  onSubmitBid,
  isSubmittingBid = false,
  submitBidStatus = null,
  isSubmitBidResultPending = false,
  onResolvedSubmitBidUsesLinehaul,
  onSelectedPortalChange,
  normalizedLoadId = '',
  resolvedSubmitBidBasisResetSignal = 0,
}: SubmitBidSectionProps) => {
  const {
    serviceFeaturesEnabled: { isBiddingPortalSubmitBidEnabled },
  } = useServiceFeatures();

  // Sidebar (credential label hints)
  const {
    currentState: { inboxEmailAddress },
  } = useContext(SidebarStateContext);
  const userLoginDisplayHint = inboxEmailAddress?.trim() || null;

  // Quote + form (lane label for summary)
  const {
    quoteMode,
    quoteResponse,
    finalFlatPrice,
    carrierCost,
    carrierCostType,
    maxDistance,
    loadingDraftReply,
    selectedLTLQuotes,
  } = useQuickQuoteContext();
  const form = useFormContext<QuickQuoteInputs>();
  const formStops = form.watch('stops');

  const laneLabel = useMemo(() => {
    const responseStops = quoteResponse?.stops ?? [];
    const hasResponseCities =
      responseStops.length >= 2 && responseStops[0]?.city;

    let origin: string;
    let destination: string;

    if (hasResponseCities) {
      origin = buildStopLabel(responseStops[0]);
      destination = buildStopLabel(responseStops[responseStops.length - 1]);
    } else if (formStops?.length >= 2) {
      origin = buildStopLabel(formStops[0]) || 'Origin';
      destination =
        buildStopLabel(formStops[formStops.length - 1]) || 'Destination';
    } else {
      origin = 'Origin';
      destination = 'Destination';
    }

    return `${origin} → ${destination}`;
  }, [formStops, quoteResponse?.stops]);

  // Selected bidding portal
  const [selectedPortal, setSelectedPortal] =
    useState<Maybe<string>>(integrationName);
  const selectPortal = useCallback(
    (portal: Maybe<string>) => {
      setSelectedPortal(portal);
      onSelectedPortalChange?.(portal);
    },
    [onSelectedPortalChange]
  );

  useEffect(() => {
    if (integrationName) {
      selectPortal(integrationName);
    }
  }, [integrationName, selectPortal]);

  const activeIntegrationName = selectedPortal ?? integrationName;

  const submitBidAsyncPollNotice = useMemo(
    () => getSubmitBidPortalAsyncPollNotice(activeIntegrationName),
    [activeIntegrationName]
  );

  // Credentials for active portal
  const cachedCredentialOptionsForActivePortal =
    activeIntegrationName != null
      ? credentialOptionsByPortal[activeIntegrationName]
      : undefined;

  const cachedCredentialOptionsForHook =
    useMemo((): MaybeUndef<CredentialOptionsResponse> => {
      if (isPortalCredentialBatchLoading) {
        return null;
      }

      if (activeIntegrationName == null) {
        return undefined;
      }

      return (
        cachedCredentialOptionsForActivePortal ??
        EMPTY_CREDENTIAL_OPTIONS_RESPONSE
      );
    }, [
      activeIntegrationName,
      cachedCredentialOptionsForActivePortal,
      isPortalCredentialBatchLoading,
    ]);

  const {
    credentials,
    integrationId: portalIntegrationId,
    selectedCredentialId,
    recommendedCredentialId,
    setSelectedCredentialId,
    isLoading: isLoadingCredentials,
  } = useCredentialOptions(activeIntegrationName, tmsCustomerId, customerName, {
    cachedCredentialOptions: cachedCredentialOptionsForHook,
  });

  const [resolvedSubmitBidUsesLinehaul, setResolvedSubmitBidUsesLinehaul] =
    useState<Maybe<boolean>>(null);
  const [
    isResolvingSubmitBidUsesLinehaul,
    setIsResolvingSubmitBidUsesLinehaul,
  ] = useState(false);

  const prevResolvedBasisResetSignal = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (prevResolvedBasisResetSignal.current === undefined) {
      prevResolvedBasisResetSignal.current = resolvedSubmitBidBasisResetSignal;
      return;
    }
    if (
      resolvedSubmitBidBasisResetSignal !== prevResolvedBasisResetSignal.current
    ) {
      prevResolvedBasisResetSignal.current = resolvedSubmitBidBasisResetSignal;
      setResolvedSubmitBidUsesLinehaul(null);
    }
  }, [resolvedSubmitBidBasisResetSignal]);

  useEffect(() => {
    let cancelled = false;

    // Resolve whether this portal integration expects line-haul vs total-sell for the bid
    // amount field. Backend answers via GET .../bid-rate-config/resolve. we cache the
    // boolean in state for the submit UI. We skip resolution when we don't have stable identifiers
    // avoiding pointless calls and clearing any previous resolution while inputs are invalid.
    if (
      portalIntegrationId == null ||
      portalIntegrationId <= 0 ||
      !normalizedLoadId.trim()
    ) {
      setIsResolvingSubmitBidUsesLinehaul(false);
      setResolvedSubmitBidUsesLinehaul(null);
      return;
    }

    setIsResolvingSubmitBidUsesLinehaul(true);

    const tmsRaw = tmsCustomerId?.trim();
    let parsedTmsCustomerId: Undef<number>;
    if (tmsRaw) {
      const parsed = Number.parseInt(tmsRaw, 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        parsedTmsCustomerId = parsed;
      }
    }

    const trimmedCustomerName = customerName?.trim();
    const trimmedLoadId = normalizedLoadId.trim();

    const bidRateResolutionRequest: PortalBidRateResolutionRequest = {
      integrationId: portalIntegrationId,
      loadId: trimmedLoadId,
    };
    if (parsedTmsCustomerId != null) {
      bidRateResolutionRequest.tmsCustomerId = parsedTmsCustomerId;
    }
    if (trimmedCustomerName) {
      bidRateResolutionRequest.customerName = trimmedCustomerName;
    }

    void (async () => {
      try {
        const result = await getPortalBidRateResolution(
          bidRateResolutionRequest
        );

        if (cancelled) {
          return;
        }

        if (result.isOk()) {
          setResolvedSubmitBidUsesLinehaul(
            result.value.bidRateBasis === PortalBidRateBasis.LineHaul
          );
        } else {
          setResolvedSubmitBidUsesLinehaul(null);
        }
      } finally {
        if (!cancelled) {
          setIsResolvingSubmitBidUsesLinehaul(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [portalIntegrationId, normalizedLoadId, tmsCustomerId, customerName]);

  useEffect(() => {
    onResolvedSubmitBidUsesLinehaul?.(resolvedSubmitBidUsesLinehaul);
  }, [onResolvedSubmitBidUsesLinehaul, resolvedSubmitBidUsesLinehaul]);

  const effectiveSubmitBidUsesLinehaul =
    resolvedSubmitBidUsesLinehaul ?? submitBidUsesLinehaul;

  const selectedCredentialLabel = useMemo(() => {
    if (selectedCredentialId == null) {
      return null;
    }

    const cred = credentials.find(
      (c) => c.credentialId === selectedCredentialId
    );
    return cred ? getCredentialDisplayLabel(cred, userLoginDisplayHint) : null;
  }, [credentials, selectedCredentialId, userLoginDisplayHint]);

  const credentialOptions = useMemo(
    () =>
      buildCredentialSelectOptions(
        credentials,
        recommendedCredentialId,
        userLoginDisplayHint
      ),
    [credentials, recommendedCredentialId, userLoginDisplayHint]
  );

  // Portal list
  const shouldShowPortalCredentialData = !isPortalCredentialBatchLoading;

  const portalLoginCounts = useMemo(() => {
    const counts: Partial<Record<QuotingPortals, number>> = {};
    for (const portal of PORTAL_VALUES) {
      const response = credentialOptionsByPortal[portal];
      counts[portal] = response?.eligibleCredentials.length ?? 0;
    }
    return counts;
  }, [credentialOptionsByPortal]);

  const portalOptions = useMemo(() => {
    const portalValues = isBiddingPortalSubmitBidEnabled
      ? getServerSubmitPortalNamesForQuoteMode(quoteMode)
      : PORTAL_VALUES;

    return portalValues
      .map((value) => ({
        label: integrationNameMap[value] ?? value,
        value,
        loginCount: portalLoginCounts[value] ?? 0,
        isRecommended: value === integrationName,
      }))
      .filter((option) => option.loginCount > 0);
  }, [
    integrationName,
    isBiddingPortalSubmitBidEnabled,
    portalLoginCounts,
    quoteMode,
  ]);

  const activePortalLabel = useMemo(
    () =>
      portalOptions.find((o) => o.value === activeIntegrationName)?.label ??
      null,
    [activeIntegrationName, portalOptions]
  );

  const portalMatchesSuggestion =
    Boolean(integrationName) && activeIntegrationName === integrationName;

  const noPortalsConfigured =
    shouldShowPortalCredentialData && portalOptions.length === 0;

  const biddingPortalSelectPlaceholder: Undef<string> = (() => {
    if (!shouldShowPortalCredentialData) {
      return 'Loading portals...';
    }

    if (portalOptions.length === 0) {
      return 'No portals available';
    }
    return undefined;
  })();

  useEffect(() => {
    if (!shouldShowPortalCredentialData) {
      return;
    }

    if (portalOptions.length === 0) {
      selectPortal(null);
      return;
    }

    if (integrationName == null) {
      if (selectedPortal != null) {
        selectPortal(null);
      }
      return;
    }

    const stillListed =
      activeIntegrationName != null &&
      portalOptions.some((o) => o.value === activeIntegrationName);

    if (stillListed) {
      return;
    }

    selectPortal(portalOptions[0]?.value ?? null);
  }, [
    activeIntegrationName,
    integrationName,
    shouldShowPortalCredentialData,
    portalOptions,
    selectedPortal,
    selectPortal,
  ]);

  // Buy / sell / margin (submit payload + summary)
  const isLtlWithSelectedQuote =
    quoteMode === QuoteMode.LTL && selectedLTLQuotes.length > 0;

  const buyRate = (() => {
    if (isLtlWithSelectedQuote) {
      const first = selectedLTLQuotes[0];
      return first.rateMetadata?.buyRateTotal ?? first.totalCost;
    }

    return calculateFlatCarrierCost(
      carrierCost ?? 0,
      carrierCostType ?? CarrierCostType.Flat,
      maxDistance ?? 0
    );
  })();

  const sellRate = isLtlWithSelectedQuote
    ? getLTLSellRate(selectedLTLQuotes[0])
    : (finalFlatPrice ?? 0);
  const margin = sellRate - buyRate;

  const portalBidAmountStrings = useMemo(() => {
    if (sellRate <= 0) {
      return null;
    }

    return buildPortalSubmitBidAmountStrings(sellRate, {
      quoteMode,
      fuelEstimate:
        quoteMode === QuoteMode.TL && fuelEstimate > 0
          ? fuelEstimate
          : undefined,
      ltlQuote: isLtlWithSelectedQuote ? selectedLTLQuotes[0] : undefined,
    });
  }, [
    fuelEstimate,
    isLtlWithSelectedQuote,
    quoteMode,
    selectedLTLQuotes,
    sellRate,
  ]);

  const submitBidPayload = (): SubmitBidPayload => ({
    integrationName: activeIntegrationName,
    credentialId: selectedCredentialId,
    integrationId: portalIntegrationId,
    selectedLtlQuoteId: isLtlWithSelectedQuote
      ? (selectedLTLQuotes[0]?.id ?? null)
      : null,
    buyRate,
    sellRate,
    margin,
    effectiveSubmitBidUsesLinehaul,
  });

  // Parent keeps `isSubmittingToPortal` true for the whole submit-bid async chain
  // (bid-rate resolution + portal submit), so the spinner stays up until completion.
  const submitBidButtonBusy = isSubmittingBid;
  const hasNormalizedLoadId = normalizedLoadId.trim().length > 0;
  const hasPositiveSellRate = sellRate > 0;
  const hasUsablePortals = portalOptions.length > 0;
  const hasSelectedCredential =
    selectedCredentialId != null && selectedCredentialId > 0;

  const submitBidPrerequisitesMet =
    hasPositiveSellRate &&
    hasNormalizedLoadId &&
    shouldShowPortalCredentialData &&
    hasUsablePortals &&
    hasSelectedCredential;

  const submitBidButtonDisabled =
    submitBidButtonBusy ||
    isResolvingSubmitBidUsesLinehaul ||
    loadingDraftReply ||
    isLoadingCredentials ||
    !submitBidPrerequisitesMet;

  // Section visibility
  const canShowSection =
    quoteMode === QuoteMode.TL ||
    (isBiddingPortalSubmitBidEnabled &&
      getServerSubmitPortalNamesForQuoteMode(quoteMode).length > 0);

  if (!canShowSection) {
    return null;
  }

  return (
    <Flex direction='col' gap='sm' className='w-full'>
      <Typography
        variant='h5'
        weight='semibold'
        className='text-neutral-800 whitespace-nowrap'
      >
        Submit bid
      </Typography>

      <Flex direction='col' gap='sm' className='w-full'>
        <Flex direction='col' gap='xs' className='w-full min-w-0'>
          <Flex
            align='center'
            gap='xs'
            className='min-w-0 text-sm text-neutral-800'
          >
            {noPortalsConfigured ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span className='inline-flex cursor-help'>
                    <Typography
                      as='span'
                      variant='body-sm'
                      weight='normal'
                      className='text-neutral-800 shrink min-w-0'
                    >
                      Bidding portal
                    </Typography>
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side='top'
                  className='w-[240px] max-w-full ml-1'
                >
                  <Typography
                    variant='body-xs'
                    className='whitespace-normal wrap-break-word text-neutral-700'
                  >
                    {NO_PORTALS_CONFIGURED_TOOLTIP_MESSAGE}
                  </Typography>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Typography
                as='span'
                variant='body-sm'
                weight='medium'
                className='text-neutral-800 shrink min-w-0'
              >
                Bidding portal
              </Typography>
            )}
            {portalMatchesSuggestion ? <AIFilledHintDecoration /> : null}
          </Flex>
          <Select
            value={activeIntegrationName ?? ''}
            onChange={(val: SelectValue) => selectPortal(String(val))}
            options={portalOptions}
            disabled={portalOptions.length === 0}
          >
            <SelectTrigger
              className={SUBMIT_BID_SIDEBAR_SELECT_TRIGGER_CLASSNAME}
              title={activePortalLabel ?? undefined}
              disabled={portalOptions.length === 0}
            >
              <SelectValue placeholder={biddingPortalSelectPlaceholder}>
                {activePortalLabel ?? null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent
              className='z-9999999 min-w-[200px]'
              position='popper'
            />
          </Select>
          {noPortalsConfigured ? (
            <Typography variant='body-xxs' className='text-neutral-500'>
              Contact support@drumkit.ai to configure bidding portal
              integrations.
            </Typography>
          ) : null}
        </Flex>

        <Flex direction='col' gap='xs' className='w-full min-w-0'>
          <Flex
            align='center'
            gap='xs'
            className='min-w-0 text-sm text-neutral-800'
          >
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className='inline-flex cursor-help'>
                  <Typography
                    as='span'
                    variant='body-sm'
                    weight='normal'
                    className='text-neutral-800'
                  >
                    Login
                  </Typography>
                </span>
              </TooltipTrigger>
              <TooltipContent side='top' className='w-[240px] max-w-full ml-1'>
                <Typography
                  variant='body-xs'
                  className='whitespace-normal wrap-break-word text-neutral-700'
                >
                  {LOGIN_HELP_TOOLTIP_MESSAGE}
                </Typography>
              </TooltipContent>
            </Tooltip>
            {recommendedCredentialId != null &&
            selectedCredentialId != null &&
            selectedCredentialId === recommendedCredentialId ? (
              <AIFilledHintDecoration />
            ) : null}
          </Flex>
          {isLoadingCredentials ? (
            <Flex align='center' gap='xs' className='h-8 px-3'>
              <ButtonLoader />
              <Typography variant='body-xs' className='text-neutral-500'>
                Loading credentials...
              </Typography>
            </Flex>
          ) : (
            <Select
              key={`${activeIntegrationName ?? 'p'}-login-${credentials.map((c) => c.credentialId).join('-') || '0'}`}
              value={
                selectedCredentialId != null && selectedCredentialId > 0
                  ? String(selectedCredentialId)
                  : undefined
              }
              onChange={(val: SelectValue) => {
                const id = Number(val);
                if (!isNaN(id)) {
                  setSelectedCredentialId(id);
                }
              }}
              options={credentialOptions}
              disabled={credentials.length === 0}
            >
              <SelectTrigger
                className={SUBMIT_BID_SIDEBAR_SELECT_TRIGGER_CLASSNAME}
                title={selectedCredentialLabel ?? undefined}
                disabled={credentials.length === 0}
              >
                <SelectValue
                  placeholder={
                    credentials.length === 0
                      ? 'No credentials available'
                      : 'Select credential'
                  }
                >
                  {selectedCredentialLabel ?? null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent
                className='z-9999999 min-w-[200px]'
                position='popper'
              />
            </Select>
          )}
        </Flex>

        <QuickQuoteTextInput
          name='loadId'
          label='Load ID'
          placeholder='Portal load number'
          inputClassName='placeholder:text-[12px]'
        />
      </Flex>

      <Flex direction='col' gap='sm' className='w-full pt-2'>
        <Flex direction='col' gap='lg' className='w-full'>
          <Flex
            direction='col'
            gap='xs'
            className='w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2'
          >
            <Typography
              variant='body-xs'
              weight='semibold'
              className='text-neutral-600'
            >
              Summary
            </Typography>
            <Flex
              justify='between'
              align='start'
              gap='md'
              className='w-full min-w-0'
            >
              <Typography
                as='span'
                variant='body-xs'
                className='shrink-0 text-neutral-500'
              >
                Lane
              </Typography>
              <Typography
                as='span'
                variant='body-xs'
                className='min-w-0 text-right text-neutral-500 wrap-break-word'
              >
                {laneLabel}
              </Typography>
            </Flex>
            {!effectiveSubmitBidUsesLinehaul ? (
              <Flex
                justify='between'
                align='baseline'
                gap='md'
                className='w-full'
              >
                <Typography
                  as='span'
                  variant='body-xs'
                  className='shrink-0 text-neutral-500'
                >
                  Sell rate
                </Typography>
                <Typography
                  as='span'
                  variant='body-xs'
                  className='tabular-nums text-neutral-500'
                >
                  {formatCurrency(sellRate, 'USD')}
                </Typography>
              </Flex>
            ) : null}
            {portalBidAmountStrings ? (
              <Flex
                justify='between'
                align='baseline'
                gap='md'
                className='w-full'
              >
                <Typography
                  as='span'
                  variant='body-xs'
                  className='shrink-0 text-neutral-500'
                >
                  {effectiveSubmitBidUsesLinehaul ? 'Line haul' : 'Total sell'}
                </Typography>
                <Typography
                  as='span'
                  variant='body-xs'
                  className='tabular-nums text-neutral-500'
                >
                  {formatCurrency(
                    Number.parseFloat(
                      effectiveSubmitBidUsesLinehaul
                        ? portalBidAmountStrings.lineHaulAmount
                        : portalBidAmountStrings.totalSellAmount
                    ),
                    'USD'
                  )}
                </Typography>
              </Flex>
            ) : null}
          </Flex>

          <Button
            buttonNamePosthog={ButtonNamePosthog.SubmitTotalToPortal}
            type='button'
            size='sm'
            className='w-full text-sm'
            disabled={submitBidButtonDisabled}
            aria-busy={submitBidButtonBusy}
            onClick={() => {
              void onSubmitBid?.(submitBidPayload());
            }}
          >
            {submitBidButtonBusy ? (
              <ButtonLoader className='mr-0' />
            ) : (
              'Submit bid'
            )}
          </Button>
        </Flex>

        {isSubmitBidResultPending && submitBidAsyncPollNotice ? (
          <Flex justify='center' align='center' className='w-full'>
            <Typography
              as='span'
              variant='body-xxs'
              aria-live='polite'
              className='text-center text-success-600'
            >
              {submitBidAsyncPollNotice}
            </Typography>
          </Flex>
        ) : null}

        {submitBidStatus?.message ? (
          <Flex justify='center' align='center' className='w-full'>
            <Typography
              as='span'
              variant='body-xxs'
              aria-live='polite'
              className={submitBidStatusVariantTextClassName(
                submitBidStatus.variant
              )}
            >
              {submitBidStatus.message}
            </Typography>
          </Flex>
        ) : null}

        <Flex direction='col' gap='xs' className='w-full'>
          <Typography
            as='span'
            variant='body-xxs'
            className='block text-center italic text-neutral-500'
          >
            Note: Bidding submissions can take up to 3 minutes.
          </Typography>
        </Flex>
      </Flex>
    </Flex>
  );
};
